import { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Plus, Trash2, Loader2, AlertCircle, ChevronDown, ChevronUp,
  DollarSign, FileText, Building2, Wrench, Package, Calculator, ClipboardList, Ban,
  User as UserIcon,
} from 'lucide-react';
import PersonnelPickerModal from '@/components/PersonnelPickerModal';
import { validateAARRows, rowsHaveErrors, todayEasternStr } from '@/utils/aarValidation';
import { formatServiceType } from '@/config/serviceTypes';
import { useAuth } from '@/contexts/AuthContext';


// Today as YYYY-MM-DD — constrains past-only date pickers.
const TODAY_ISO = (() => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
})();

// Load Status Options — aligned with SR + Railcar Repair Status Notification
const LOAD_STATUSES = [
  { value: 'CLEAN', label: 'Clean' },
  { value: 'EMPTY', label: 'Empty' },
  { value: 'RESIDUE', label: 'Residue' },
  { value: 'LOADED', label: 'Loaded' },
  { value: 'OTHER', label: 'Other' }
];

// Additional Charge Types — alphabetically sorted. Backend stores
// `charge_type` as a free-form string (no enum), so adding new options here
// requires no backend changes.
const CHARGE_TYPES = [
  { value: 'CONSUMABLES', label: 'Consumables' },
  { value: 'EQUIPMENT', label: 'Equipment' },
  { value: 'FUEL', label: 'Fuel' },
  { value: 'MATERIALS', label: 'Materials' },
  { value: 'OTHER', label: 'Other' },
  { value: 'SAFETY_ATTENDANT', label: 'Safety Attendant' },
  { value: 'SHOP_MATERIALS', label: 'Shop Materials' },
  { value: 'SPECIALTY_PPE', label: 'Specialty PPE' },
  { value: 'SUBCONTRACTOR', label: 'Subcontractor' },
  { value: 'TRAVEL', label: 'Travel' }
];

// Reporting Data Types
const REPORTING_DATA_TYPES = [
  { value: 'CID', label: 'CID' },
  { value: 'WELDING', label: 'Welding' }
];

// Standardized weld positions (AWS D1.1 / AAR)
const WELD_POSITIONS = [
  "1F", "2F", "3F", "4F",
  "1G", "2G", "3G", "4G", "5G", "6G"
];

// Standardized welding processes (alphabetical)
const WELD_PROCESSES = ["FCAW", "GMAW", "GTAW", "SMAW"];

// Standardized CID component types (alphabetical)
const CID_COMPONENT_TYPES = [
  "Bolster",
  "Brake Valve Control Emergency",
  "Brake Valve Control Service",
  "Coupler",
  "Cushioning Unit",
  "Pressure Relief Device",
  "Side Frame",
  "Slack Adjuster",
  "Wheelset"
];

// AAR Work Details Field Configuration (in required order)
// Field order: date, wdb, description, loc, qty, jc_app, cc, qfa, wm, jc_rem, qfr, rc, hrs, qvb, qa_date
//
// Phase 2 polish (2026-06-01): helper placeholders removed per UAT. Fields
// render as plain inputs; format hints live in the per-cell validation messages.
const AAR_FIELDS = [
  { key: 'work_date', label: 'DATE', type: 'date', width: 'w-28' },
  { key: 'wdb', label: 'WDB', type: 'personnel', width: 'w-32' },
  { key: 'description', label: 'DESCRIPTION', type: 'text', width: 'w-48' },
  { key: 'loc', label: 'LOC', type: 'text', width: 'w-20' },
  { key: 'qty', label: 'QTY', type: 'text', width: 'w-16' },
  { key: 'jc_app', label: 'JC APP', type: 'text', width: 'w-24' },
  { key: 'cc', label: 'CC', type: 'text', width: 'w-16' },
  { key: 'qfa', label: 'QFA', type: 'text', width: 'w-16' },
  { key: 'wm', label: 'WM', type: 'text', width: 'w-16' },
  { key: 'jc_rem', label: 'JC REM', type: 'text', width: 'w-24' },
  { key: 'qfr', label: 'QFR', type: 'text', width: 'w-16' },
  { key: 'rc', label: 'RC', type: 'text', width: 'w-16' },
  { key: 'hrs', label: 'HRS', type: 'text', width: 'w-20' },
  { key: 'qvb', label: 'QVB', type: 'personnel', width: 'w-32' },
  { key: 'qa_date', label: 'QA DATE', type: 'date', width: 'w-28' },
];

// Default empty AAR row
// work_date defaults to "today in US Eastern" so a freshly-added row never
// trips the new no-future-date validation when the server's UTC clock has
// rolled into the next day but Eastern is still on the prior business day.
const createEmptyAARRow = () => ({
  work_date: todayEasternStr(),
  wdb: '',
  wdb_user_id: null,
  description: '',
  loc: '',
  qty: '',
  jc_app: '',
  cc: '',
  qfa: '',
  wm: '',
  jc_rem: '',
  qfr: '',
  rc: '',
  hrs: '',
  qvb: '',
  qvb_user_id: null,
  qa_date: '',
  row_status: 'ACTIVE'
});

// BRC Status Badge
function StatusBadge({ status }) {
  const statusColors = {
    DRAFT: 'bg-gray-100 text-gray-700',
    SUBMITTED: 'bg-blue-100 text-blue-700',
    INVOICED: 'bg-green-100 text-green-700',
    VOIDED: 'bg-red-100 text-red-700'
  };
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[status] || statusColors.DRAFT}`}>
      {status || 'SUBMITTED'}
    </span>
  );
}

// Material Line Component - New 6-field structure
// Fields: associated_job_code, part_number, description, qty, unit_price, amount
function MaterialLineRow({ line, index, onChange, onRemove, readOnly, canSeeAmounts, activeJobCodes = [], error }) {
  const handleChange = (field, value) => {
    const updated = { ...line, [field]: value };
    // Recalculate amount when qty or unit_price changes
    if ((field === 'qty' || field === 'unit_price') && canSeeAmounts) {
      updated.amount = (parseFloat(updated.qty) || 0) * (parseFloat(updated.unit_price) || 0);
    }
    onChange(index, updated);
  };

  // Column spans based on whether user can see amounts:
  // With amounts: job_code(2) + part_num(2) + desc(3) + qty(1) + price(2) + amount(1) + action(1) = 12
  // Without amounts: job_code(2) + part_num(2) + desc(4) + qty(2) + spacer(1) + action(1) = 12

  const noJobCodes = activeJobCodes.length === 0;
  const errorBorder = error ? 'border-red-500 ring-1 ring-red-500' : '';

  return (
    <div 
      className={`grid gap-2 items-start py-2 border-b last:border-b-0 ${canSeeAmounts ? 'grid-cols-12' : 'grid-cols-12'}`} 
      data-testid={`material-row-${index}`}
    >
      {/* Associated Job Code - Required - Restricted to AAR Work Details job codes */}
      <div className={canSeeAmounts ? "col-span-2" : "col-span-2"}>
        <select
          value={line.associated_job_code || ''}
          onChange={(e) => handleChange('associated_job_code', e.target.value)}
          disabled={readOnly || noJobCodes}
          className={`w-full h-9 px-2 text-sm border rounded-md bg-background ${errorBorder}`}
          data-testid={`material-jobcode-${index}`}
        >
          <option value="">Select job code</option>
          {activeJobCodes.map(jc => (
            <option key={jc} value={jc}>{jc}</option>
          ))}
        </select>
        {error && (
          <p className="text-xs text-red-600 mt-1" data-testid={`material-jobcode-error-${index}`}>{error}</p>
        )}
      </div>
      
      {/* Part Number - Required */}
      <div className={canSeeAmounts ? "col-span-2" : "col-span-2"}>
        <Input 
          value={line.part_number || ''} 
          onChange={(e) => handleChange('part_number', e.target.value)}
          placeholder="Part # *"
          className="text-sm"
          disabled={readOnly}
          required
          data-testid={`material-partnum-${index}`}
        />
      </div>
      
      {/* Description */}
      <div className={canSeeAmounts ? "col-span-3" : "col-span-4"}>
        <Input 
          value={line.description || ''} 
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Material description"
          className="text-sm"
          disabled={readOnly}
          data-testid={`material-desc-${index}`}
        />
      </div>
      
      {/* Quantity */}
      <div className={canSeeAmounts ? "col-span-1" : "col-span-2"}>
        <Input 
          type="number" 
          step="1"
          min="1"
          value={line.qty || ''} 
          onChange={(e) => handleChange('qty', parseFloat(e.target.value) || 0)}
          placeholder="Qty"
          className="text-sm text-right"
          disabled={readOnly}
          data-testid={`material-qty-${index}`}
        />
      </div>
      
      {/* Unit Price & Amount - Only visible to Operator+ */}
      {canSeeAmounts && (
        <>
          <div className="col-span-2">
            <Input 
              type="number" 
              step="0.01"
              min="0"
              value={line.unit_price || ''} 
              onChange={(e) => handleChange('unit_price', parseFloat(e.target.value) || 0)}
              placeholder="Unit $"
              className="text-sm text-right"
              disabled={readOnly}
              data-testid={`material-price-${index}`}
            />
          </div>
          <div className="col-span-1 text-right">
            <span className="text-sm font-medium text-[#323033]">${(line.amount || 0).toFixed(2)}</span>
          </div>
        </>
      )}
      
      {/* Spacer for non-amount users */}
      {!canSeeAmounts && <div className="col-span-1"></div>}
      
      {/* Remove Button */}
      <div className="col-span-1 text-right">
        {!readOnly && (
          <Button 
            type="button" 
            variant="ghost" 
            size="sm" 
            onClick={() => onRemove(index)}
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
            data-testid={`material-remove-${index}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

// Additional Charge Row Component
function ChargeRow({ charge, index, onChange, onRemove, readOnly, canSeeAmounts }) {
  const handleChange = (field, value) => {
    const updated = { ...charge, [field]: value };
    if ((field === 'quantity' || field === 'unit_price') && canSeeAmounts) {
      updated.amount = (parseFloat(updated.quantity) || 0) * (parseFloat(updated.unit_price) || 0);
    }
    onChange(index, updated);
  };

  return (
    <div className="grid grid-cols-12 gap-2 items-center py-2 border-b last:border-b-0" data-testid={`charge-row-${index}`}>
      <div className="col-span-2">
        <select 
          value={charge.charge_type || ''} 
          onChange={(e) => handleChange('charge_type', e.target.value)}
          className="w-full h-9 px-2 text-sm border rounded-md"
          disabled={readOnly}
          data-testid={`charge-type-${index}`}
        >
          <option value="">Select Type</option>
          {CHARGE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>
      <div className="col-span-4">
        <Input 
          value={charge.description || ''} 
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Description"
          className="text-sm"
          disabled={readOnly}
          data-testid={`charge-desc-${index}`}
        />
      </div>
      <div className="col-span-2">
        <Input 
          type="number" 
          step="0.5"
          value={charge.quantity || ''} 
          onChange={(e) => handleChange('quantity', parseFloat(e.target.value) || 0)}
          placeholder="Qty"
          className="text-sm text-right"
          disabled={readOnly}
          data-testid={`charge-qty-${index}`}
        />
      </div>
      {canSeeAmounts && (
        <>
          <div className="col-span-2">
            <Input 
              type="number" 
              step="0.01"
              value={charge.unit_price || ''} 
              onChange={(e) => handleChange('unit_price', parseFloat(e.target.value) || 0)}
              placeholder="Rate"
              className="text-sm text-right"
              disabled={readOnly}
              data-testid={`charge-rate-${index}`}
            />
          </div>
          <div className="col-span-1">
            <span className="text-sm font-medium">${(charge.amount || 0).toFixed(2)}</span>
          </div>
        </>
      )}
      {!canSeeAmounts && <div className="col-span-3"></div>}
      <div className="col-span-1 text-right">
        {!readOnly && (
          <Button 
            type="button" 
            variant="ghost" 
            size="sm" 
            onClick={() => onRemove(index)}
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
            data-testid={`charge-remove-${index}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

// Main Billing Entry Form Component
export default function BillingEntryForm({ 
  brc = null, 
  context = {}, 
  onSave, 
  onCancel, 
  isNew = true,
  readOnly = false 
}) {
  const { api } = useAuth();
  const [saving, setSaving] = useState(false);
  // Phase 2 polish (2026-06-01): required-field validation is "lazy" — errors
  // only surface after the user attempts Save. Once attempted, the highlights
  // remain until each field is corrected.
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [error, setError] = useState(null);
  const [materialErrors, setMaterialErrors] = useState({});
  const [reportingErrors, setReportingErrors] = useState({});
  // Phase 2 — WDB/QVB picker state and per-row format errors. The validation
  // rules only apply to BRCs at validation_version >= 2 (i.e. new BRCs); for
  // legacy BRCs we display empty error objects and let the backend decide.
  const [pickerState, setPickerState] = useState(null);
  // {rowIndex, field: 'WDB'|'QVB'} or null when closed
  const [expandedSections, setExpandedSections] = useState({
    aar: true,
    materials: false,
    reportingData: true,
    charges: true,
    invoice: true
  });
  const tableContainerRef = useRef(null);
  
  // Permission context - Technicians cannot see dollar amounts
  const canSeeAmounts = context.can_see_amounts !== false;
  
  // Determine if BRC is submitted (not DRAFT)
  const isSubmitted = brc?.status && brc.status !== 'DRAFT';
  
  // Convert legacy single-object aar_work_details to array format for display
  const initializeAARItems = () => {
    // Check for new array format first
    if (brc?.aar_work_details_items && Array.isArray(brc.aar_work_details_items) && brc.aar_work_details_items.length > 0) {
      return brc.aar_work_details_items;
    }
    // Fall back to legacy single-object format
    if (brc?.aar_work_details && typeof brc.aar_work_details === 'object' && !Array.isArray(brc.aar_work_details)) {
      const hasData = Object.keys(brc.aar_work_details).some(key =>
        brc.aar_work_details[key] && brc.aar_work_details[key] !== ''
      );
      if (hasData) {
        return [{ ...brc.aar_work_details, row_status: 'ACTIVE' }];
      }
    }
    // Phase 2 polish (2026-06-01): do NOT pre-create a blank row. Users explicitly
    // click "Add Line Item" to start an AAR row.
    return [];
  };
  
  // Form state
  const [formData, setFormData] = useState({
    // Responding facility
    responding_facility_id: brc?.responding_facility_id || context.default_facility_id || '',
    
    // Car specification
    car_specification: brc?.car_specification || '',
    load_status: brc?.load_status || 'EMPTY',
    load_status_other: brc?.load_status_other || '',
    site_name: brc?.site_name || context.site_name || '',
    
    // AAR Work Details Items (NEW array format - multiple line items)
    aar_work_details_items: initializeAARItems(),
    
    // Material lines (optional per-job-code)
    material_lines: brc?.material_lines || [],
    
    // Reporting Data (CID/Welding entries)
    reporting_data: brc?.reporting_data || [],
    
    // Additional charges
    additional_charges: brc?.additional_charges || [],
    
    // Invoice To
    invoice_to_company: brc?.invoice_to_company || context.invoice_to_company || '',
    invoice_to_contact: brc?.invoice_to_contact || context.invoice_to_contact || '',
    invoice_to_address: brc?.invoice_to_address || '',
    invoice_to_city: brc?.invoice_to_city || '',
    invoice_to_state: brc?.invoice_to_state || '',
    invoice_to_zip: brc?.invoice_to_zip || '',
    invoice_to_email: brc?.invoice_to_email || context.invoice_to_email || '',
    
    // Date and notes
    date: brc?.date || new Date().toISOString().split('T')[0],
    notes: brc?.notes || '',
    
    // Monetary fields (only visible to Operator+)
    labor_rate: brc?.labor_rate || 0,
    labor_total: brc?.labor_total || 0,
    material_total: brc?.material_total || 0,
    charges_total: brc?.charges_total || 0,
    grand_total: brc?.grand_total || 0,
    
    // Edit reason (for INVOICED BRCs)
    edit_reason: ''
  });

  // Calculate totals when amounts change (only for users who can see amounts)
  useEffect(() => {
    if (canSeeAmounts) {
      // Sum hours from all ACTIVE AAR items (QTY × HRS per row)
      const totalHrs = formData.aar_work_details_items
        .filter(item => item.row_status !== 'VOIDED')
        .reduce((sum, item) => {
          const qty = parseFloat(item.qty) || 1; // Default to 1 if blank
          const hrs = parseFloat(item.hrs) || 0;
          return sum + (qty * hrs);
        }, 0);
      
      const rate = parseFloat(formData.labor_rate) || 0;
      const laborTotal = totalHrs * rate;
      
      const materialTotal = formData.material_lines.reduce(
        (sum, line) => sum + (parseFloat(line.amount) || 0), 0
      );
      
      const chargesTotal = formData.additional_charges.reduce(
        (sum, charge) => sum + (parseFloat(charge.amount) || 0), 0
      );
      
      const grandTotal = laborTotal + materialTotal + chargesTotal;
      
      setFormData(prev => ({
        ...prev,
        labor_total: laborTotal,
        material_total: materialTotal,
        charges_total: chargesTotal,
        grand_total: grandTotal
      }));
    }
  }, [
    formData.aar_work_details_items,
    formData.labor_rate,
    formData.material_lines,
    formData.additional_charges,
    canSeeAmounts
  ]);

  // Update field
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Phase 2 — A BRC is "new format" (subject to strict validation) when:
  //   • we're creating one (isNew), OR
  //   • the loaded BRC carries validation_version >= 2.
  // Legacy BRCs (no validation_version) keep their pre-Phase-2 free-text behavior.
  const isPhase2 = isNew || (brc?.validation_version || 0) >= 2;

  // Per-row format errors. Memoized so error lookup in the render loop is cheap.
  const aarErrors = useMemo(
    () => (isPhase2 ? validateAARRows(formData.aar_work_details_items) : []),
    [formData.aar_work_details_items, isPhase2]
  );
  const aarHasErrors = isPhase2 && rowsHaveErrors(aarErrors);
  // Phase 2 polish: top-level required car_specification participates in
  // save-time validation alongside per-row AAR errors.
  const carSpecMissing = isPhase2 && !formData.car_specification?.trim();
  const formHasErrors = aarHasErrors || carSpecMissing;

  // Personnel picker — open with {rowIndex, field: 'WDB'|'QVB'}, close with null.
  const openPersonnelPicker = (rowIndex, field) => setPickerState({ rowIndex, field });
  const closePersonnelPicker = () => setPickerState(null);
  const handlePersonnelSelect = (person) => {
    if (!pickerState) return;
    const { rowIndex, field } = pickerState;
    setFormData(prev => ({
      ...prev,
      aar_work_details_items: prev.aar_work_details_items.map((row, i) => {
        if (i !== rowIndex) return row;
        if (field === 'WDB') return { ...row, wdb_user_id: person.id, wdb: person.display };
        if (field === 'QVB') return { ...row, qvb_user_id: person.id, qvb: person.display };
        return row;
      })
    }));
  };

  // AAR Work Details Items handlers (NEW array format)
  const addAARRow = () => {
    setFormData(prev => ({
      ...prev,
      aar_work_details_items: [...prev.aar_work_details_items, createEmptyAARRow()]
    }));
  };

  const updateAARRow = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      aar_work_details_items: prev.aar_work_details_items.map((row, i) => 
        i === index ? { ...row, [field]: value } : row
      )
    }));
  };

  const removeAARRow = (index) => {
    // For draft BRCs, actually remove the row
    // For submitted BRCs, mark as VOIDED instead
    if (!isSubmitted) {
      // Draft BRC: hard delete row
      setFormData(prev => ({
        ...prev,
        aar_work_details_items: prev.aar_work_details_items.filter((_, i) => i !== index)
      }));
    } else {
      // Submitted BRC: mark as VOIDED (soft delete)
      setFormData(prev => ({
        ...prev,
        aar_work_details_items: prev.aar_work_details_items.map((row, i) => 
          i === index ? { ...row, row_status: 'VOIDED' } : row
        )
      }));
    }
  };

  // Material line handlers
  const addMaterialLine = () => {
    setFormData(prev => ({
      ...prev,
      material_lines: [...prev.material_lines, { 
        _key: crypto.randomUUID(),
        associated_job_code: '', // Required - links to AAR job code
        part_number: '',         // Required - inventory part number
        description: '',         // Material description
        qty: 1,                  // Quantity
        unit_price: 0, 
        amount: 0 
      }]
    }));
    setExpandedSections(prev => ({ ...prev, materials: true }));
  };

  const updateMaterialLine = (index, updated) => {
    setFormData(prev => ({
      ...prev,
      material_lines: prev.material_lines.map((line, i) => i === index ? updated : line)
    }));
    setMaterialErrors(prev => {
      if (!(index in prev)) return prev;
      const { [index]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const removeMaterialLine = (index) => {
    setFormData(prev => ({
      ...prev,
      material_lines: prev.material_lines.filter((_, i) => i !== index)
    }));
  };

  // Charge row handlers
  const addChargeRow = () => {
    setFormData(prev => ({
      ...prev,
      additional_charges: [...prev.additional_charges, { 
        _key: crypto.randomUUID(),
        charge_type: '', 
        description: '', 
        quantity: 1, 
        unit_price: 0, 
        amount: 0 
      }]
    }));
  };

  const updateChargeRow = (index, updated) => {
    setFormData(prev => ({
      ...prev,
      additional_charges: prev.additional_charges.map((charge, i) => i === index ? updated : charge)
    }));
  };

  const removeChargeRow = (index) => {
    setFormData(prev => ({
      ...prev,
      additional_charges: prev.additional_charges.filter((_, i) => i !== index)
    }));
  };

  // Reporting Data handlers
  const addReportingEntry = (dataType) => {
    const activeJobCodes = formData.aar_work_details_items
      .filter(item => item.row_status !== 'VOIDED' && item.jc_app)
      .map(item => item.jc_app);
    
    const newEntry = {
      _key: crypto.randomUUID(),
      id: crypto.randomUUID(),
      data_type: dataType,
      associated_job_code: activeJobCodes[0] || '',
      ...(dataType === 'CID' ? { component_type: '', location_position: '', cid_number: '' } : {}),
      ...(dataType === 'WELDING' ? { process: '', position: '', wps: '' } : {}),
    };
    setFormData(prev => ({
      ...prev,
      reporting_data: [...prev.reporting_data, newEntry]
    }));
    setExpandedSections(prev => ({ ...prev, reportingData: true }));
  };

  const updateReportingEntry = (index, updated) => {
    setFormData(prev => ({
      ...prev,
      reporting_data: prev.reporting_data.map((entry, i) => i === index ? { ...entry, ...updated } : entry)
    }));
    if ('associated_job_code' in updated) {
      setReportingErrors(prev => {
        if (!(index in prev)) return prev;
        const { [index]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const removeReportingEntry = (index) => {
    setFormData(prev => ({
      ...prev,
      reporting_data: prev.reporting_data.filter((_, i) => i !== index)
    }));
  };

  const getActiveJobCodes = () => {
    return formData.aar_work_details_items
      .filter(item => item.row_status !== 'VOIDED' && item.jc_app)
      .map(item => item.jc_app);
  };

  // Toggle section
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Calculate total hours across all active rows (QTY × HRS per row)
  const getTotalHours = () => {
    return formData.aar_work_details_items
      .filter(item => item.row_status !== 'VOIDED')
      .reduce((sum, item) => {
        const qty = parseFloat(item.qty) || 1; // Default to 1 if blank
        const hrs = parseFloat(item.hrs) || 0;
        return sum + (qty * hrs);
      }, 0);
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMaterialErrors({});
    setReportingErrors({});
    
    // Validation
    if (!formData.responding_facility_id) {
      setError('Responding Facility is required');
      return;
    }
    
    if (!formData.date) {
      setError('Date is required');
      return;
    }
    
    // Validate at least one active AAR row with required fields
    const activeRows = formData.aar_work_details_items.filter(row => row.row_status !== 'VOIDED');
    if (activeRows.length === 0) {
      setError('At least one AAR Work Details line item is required');
      return;
    }
    
    // Validate each active row has required fields
    // 2026-06-02: QFA and QFR are optional. Format-only validation for these
    // fields lives in aarValidation.js (Phase 2). Do not gate Save on them here.
    const requiredFields = ['work_date', 'wdb', 'description', 'loc', 'qty', 'jc_app', 'cc', 'wm', 'jc_rem', 'rc', 'hrs', 'qvb', 'qa_date'];
    for (let i = 0; i < activeRows.length; i++) {
      const row = activeRows[i];
      for (const field of requiredFields) {
        if (!row[field] || (typeof row[field] === 'string' && !row[field].trim())) {
          const fieldConfig = AAR_FIELDS.find(f => f.key === field);
          setError(`Row ${i + 1}: ${fieldConfig?.label || field} is required`);
          return;
        }
      }
    }
    
    // Check if editing INVOICED BRC requires reason
    if (brc?.status === 'INVOICED' && !formData.edit_reason) {
      setError('Edit reason is required for INVOICED Billing Entries');
      return;
    }
    
    // Validate material lines - required fields when material line exists
    // AAR Work Details is the single source of truth for valid job codes
    const validJobCodes = new Set(getActiveJobCodes());
    const newMaterialErrors = {};
    for (let i = 0; i < formData.material_lines.length; i++) {
      const line = formData.material_lines[i];
      if (!line.associated_job_code || !line.associated_job_code.trim()) {
        newMaterialErrors[i] = 'Associated Job Code is required.';
        continue;
      }
      if (!validJobCodes.has(line.associated_job_code)) {
        newMaterialErrors[i] = 'Associated Job Code must match an existing AAR work detail job code.';
        continue;
      }
      if (!line.part_number || !line.part_number.trim()) {
        setError(`Material Line ${i + 1}: Part Number is required`);
        setMaterialErrors(newMaterialErrors);
        return;
      }
    }
    if (Object.keys(newMaterialErrors).length > 0) {
      setMaterialErrors(newMaterialErrors);
      setError('One or more Material Charges lines reference a job code that no longer exists in AAR Work Details.');
      return;
    }
    
    // Validate Reporting Data entries
    // First pass: cross-validate associated_job_code against AAR Work Details (collect inline errors)
    const newReportingErrors = {};
    for (let i = 0; i < formData.reporting_data.length; i++) {
      const entry = formData.reporting_data[i];
      if (!entry.associated_job_code) {
        newReportingErrors[i] = 'Associated Job Code is required.';
        continue;
      }
      if (!validJobCodes.has(entry.associated_job_code)) {
        newReportingErrors[i] = 'Associated Job Code must match an existing AAR work detail job code.';
      }
    }
    if (Object.keys(newReportingErrors).length > 0) {
      setReportingErrors(newReportingErrors);
      setError('One or more Reporting Data entries reference a job code that no longer exists in AAR Work Details.');
      return;
    }
    // Second pass: per-type field validation (kept as before)
    for (let i = 0; i < formData.reporting_data.length; i++) {
      const entry = formData.reporting_data[i];
      if (entry.data_type === 'CID') {
        if (!entry.component_type?.trim()) {
          setError(`CID entry ${i + 1}: Component type is required.`);
          return;
        }
        if (!entry.location_position?.trim()) {
          setError(`CID entry ${i + 1}: Location/Position is required.`);
          return;
        }
        if (entry.location_position.length > 2) {
          setError(`CID entry ${i + 1}: Location/Position must be 1-2 characters.`);
          return;
        }
        if (!/^[a-zA-Z0-9]+$/.test(entry.location_position)) {
          setError(`CID entry ${i + 1}: Location/Position must be letters and numbers only.`);
          return;
        }
        if (!entry.cid_number?.trim()) {
          setError(`CID entry ${i + 1}: CID # is required.`);
          return;
        }
        if (!/^[a-zA-Z0-9]+$/.test(entry.cid_number)) {
          setError(`CID entry ${i + 1}: CID # must be letters and numbers only.`);
          return;
        }
        if (entry.cid_number.length > 20) {
          setError(`CID entry ${i + 1}: CID # cannot exceed 20 characters.`);
          return;
        }
      }
      if (entry.data_type === 'WELDING') {
        if (!entry.process?.trim()) {
          setError(`Welding entry ${i + 1}: Process is required.`);
          return;
        }
        if (!entry.position) {
          setError(`Welding entry ${i + 1}: Select a weld position.`);
          return;
        }
        if (!entry.wps?.trim()) {
          setError(`Welding entry ${i + 1}: WPS is required.`);
          return;
        }
      }
    }
    
    setSaving(true);
    try {
      await onSave({
        ...formData,
        work_schedule_id: context.work_schedule_id
      });
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const isInvoiced = brc?.status === 'INVOICED';
  const isVoided = brc?.status === 'VOIDED';
  const effectiveReadOnly = readOnly || isVoided;

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="billing-entry-form">
      {/* Error Display */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Status Badge for existing BRCs */}
      {!isNew && brc && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="font-medium text-[#323033]">{brc.brc_number}</h3>
            <StatusBadge status={brc.status} />
          </div>
          {brc.submitter_name && (
            <span className="text-sm text-[#5A5A5A]">Created by {brc.submitter_name}</span>
          )}
        </div>
      )}

      {/* Section 1: Auto-populated Read-only Fields */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#005EF8]" />
            Job Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs text-[#5A5A5A]">Car Mark</Label>
              <p className="font-medium text-[#323033]">{context.car_mark || '-'}</p>
            </div>
            <div>
              <Label className="text-xs text-[#5A5A5A]">Car Number</Label>
              <p className="font-medium text-[#323033]">{context.car_number || '-'}</p>
            </div>
            <div>
              <Label className="text-xs text-[#5A5A5A]">Service Type</Label>
              <p className="font-medium text-[#323033]">{formatServiceType(context.service_type) || '-'}</p>
            </div>
            <div>
              <Label className="text-xs text-[#5A5A5A]">Car Type</Label>
              <p className="font-medium text-[#323033]">{context.car_type || '-'}</p>
            </div>
            <div>
              <Label className="text-xs text-[#5A5A5A]">SPLC Code</Label>
              <p className="font-medium text-[#323033]">{context.splc_code || '-'}</p>
            </div>
            <div>
              <Label className="text-xs text-[#5A5A5A]">Service Request #</Label>
              <p className="font-medium text-[#323033]">{context.job_number || '-'}</p>
            </div>
            <div>
              <Label className="text-xs text-[#5A5A5A]">Car Owner</Label>
              <p className="font-medium text-[#323033]">{context.car_owner || '-'}</p>
            </div>
            <div>
              <Label className="text-xs text-[#5A5A5A]">Job Lead</Label>
              <p className="font-medium text-[#323033]">{context.job_lead_name || '-'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 2: Billing Details */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#005EF8]" />
            Billing Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Responding Facility *</Label>
              <select 
                value={formData.responding_facility_id}
                onChange={(e) => updateField('responding_facility_id', e.target.value)}
                className="w-full h-10 px-3 border rounded-md"
                disabled={effectiveReadOnly}
                required
                data-testid="responding-facility-select"
              >
                <option value="">Select Facility</option>
                {(context.facilities || []).map(f => (
                  <option key={f.id} value={f.id}>
                    {f.name} {f.station_stencil ? `(${f.station_stencil})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>BRC Date *</Label>
              <Input 
                type="date" 
                value={formData.date} 
                max={TODAY_ISO}
                onChange={(e) => updateField('date', e.target.value)}
                disabled={effectiveReadOnly}
                required
                data-testid="brc-date"
              />
            </div>
            <div>
              <Label>Load Status</Label>
              <select 
                value={formData.load_status}
                onChange={(e) => updateField('load_status', e.target.value)}
                className="w-full h-10 px-3 border rounded-md"
                disabled={effectiveReadOnly}
                data-testid="load-status-select"
              >
                {LOAD_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              {formData.load_status === 'OTHER' && (
                <Input
                  className="mt-2"
                  value={formData.load_status_other}
                  onChange={(e) => updateField('load_status_other', e.target.value)}
                  disabled={effectiveReadOnly}
                  maxLength={100}
                  placeholder="Specify load status"
                  data-testid="load-status-other-input"
                />
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Car Specification *</Label>
              <Input 
                value={formData.car_specification}
                onChange={(e) => updateField('car_specification', e.target.value)}
                placeholder="e.g., DOT-111, 20K gallon"
                disabled={effectiveReadOnly}
                className={saveAttempted && !formData.car_specification?.trim() ? 'border-red-500 ring-1 ring-red-200' : ''}
                data-testid="car-spec"
              />
              {saveAttempted && !formData.car_specification?.trim() && (
                <p className="mt-0.5 text-[10px] text-red-600 leading-tight" data-testid="car-spec-error">
                  Car Specification is required
                </p>
              )}
            </div>
            <div>
              <Label>Site Name</Label>
              <Input 
                value={formData.site_name}
                onChange={(e) => updateField('site_name', e.target.value)}
                placeholder={context.site_name || 'Site name from SR'}
                disabled={effectiveReadOnly}
                data-testid="site-name"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 3: AAR Work Details (HORIZONTAL TABLE - Multiple Line Items) */}
      <Card>
        <CardHeader className="pb-3 cursor-pointer" onClick={() => toggleSection('aar')}>
          <CardTitle className="text-base flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wrench className="w-4 h-4 text-[#005EF8]" />
              AAR Work Details
              <span className="text-xs bg-[#005EF8]/10 text-[#005EF8] px-2 py-0.5 rounded-full">
                {formData.aar_work_details_items.filter(r => r.row_status !== 'VOIDED').length} line items
              </span>
            </div>
            <div className="flex items-center gap-2">
              {canSeeAmounts && (
                <span className="text-sm font-normal text-[#5A5A5A]">
                  Total: {getTotalHours().toFixed(1)} hrs
                  {formData.labor_rate > 0 && ` × $${formData.labor_rate}/hr = $${formData.labor_total.toFixed(2)}`}
                </span>
              )}
              {!canSeeAmounts && (
                <span className="text-sm font-normal text-[#5A5A5A]">
                  Total: {getTotalHours().toFixed(1)} hrs
                </span>
              )}
              {expandedSections.aar ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          </CardTitle>
        </CardHeader>
        {expandedSections.aar && (
          <CardContent className="space-y-4">
            {/* Horizontal Scrollable Table */}
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto" ref={tableContainerRef}>
                <table className="w-full min-w-[1400px]" data-testid="aar-table">
                  {/* Table Header */}
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-2 py-2 text-left text-xs font-medium text-[#5A5A5A] w-8">#</th>
                      {AAR_FIELDS.map(field => (
                        <th 
                          key={field.key} 
                          className={`px-2 py-2 text-left text-xs font-medium text-[#5A5A5A] ${field.width}`}
                        >
                          {field.label}
                        </th>
                      ))}
                      <th className="px-2 py-2 text-center text-xs font-medium text-[#5A5A5A] w-16 sticky right-0 bg-gray-50">
                        {!effectiveReadOnly && 'Actions'}
                      </th>
                    </tr>
                  </thead>
                  {/* Table Body */}
                  <tbody className="divide-y">
                    {formData.aar_work_details_items.map((row, rowIndex) => {
                      const isRowVoided = row.row_status === 'VOIDED';
                      return (
                        <tr 
                          key={rowIndex}
                          className={`${isRowVoided ? 'bg-red-50/50 opacity-60' : 'hover:bg-gray-50'}`}
                          data-testid={`aar-row-${rowIndex}`}
                        >
                          <td className="px-2 py-2 text-sm text-[#5A5A5A]">
                            {rowIndex + 1}
                            {isRowVoided && (
                              <span className="ml-1 text-xs text-red-500">(V)</span>
                            )}
                          </td>
                          {AAR_FIELDS.map(field => {
                            // Phase 2 polish: required-field errors only surface after
                            // the user attempts Save. Format errors on values they typed
                            // still show immediately so they don't accidentally save bad data.
                            const rawError = aarErrors[rowIndex]?.[field.key];
                            const cellError = saveAttempted ? rawError : null;

                            if (field.type === 'personnel') {
                              const displayVal = row[field.key] || '';
                              return (
                                <td key={field.key} className={`px-1 py-1 ${field.width} align-top`}>
                                  <button
                                    type="button"
                                    onClick={() => openPersonnelPicker(rowIndex, field.key === 'wdb' ? 'WDB' : 'QVB')}
                                    disabled={effectiveReadOnly || isRowVoided}
                                    className={`w-full text-left text-sm h-8 px-2 border rounded-md flex items-center gap-1 truncate
                                      ${cellError ? 'border-red-500 ring-1 ring-red-200' : ''}
                                      ${isRowVoided ? 'line-through bg-gray-50 text-gray-400'
                                        : effectiveReadOnly ? 'bg-gray-50 text-gray-900 cursor-default'
                                        : displayVal ? 'bg-white hover:bg-gray-50' : 'bg-white text-gray-400 hover:bg-gray-50'}`}
                                    title={cellError || ''}
                                    data-testid={`aar-${field.key}-${rowIndex}`}
                                  >
                                    <UserIcon className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                                    <span className="truncate">{displayVal || 'Select…'}</span>
                                  </button>
                                  {cellError && (
                                    <p className="absolute mt-0.5 text-[10px] text-red-600 leading-tight pointer-events-none" data-testid={`aar-${field.key}-${rowIndex}-error`}>
                                      {cellError}
                                    </p>
                                  )}
                                </td>
                              );
                            }
                            // Format-rule errors (typed values that violate regex/length)
                            // should still appear as the user types — those are not
                            // "required-only" surprises. Required-only errors are gated
                            // by saveAttempted above.
                            const formatErrorImmediate =
                              rawError && (row[field.key] || '').toString().length > 0
                                ? rawError
                                : null;
                            const visibleError = cellError || formatErrorImmediate;
                            return (
                              <td key={field.key} className={`px-1 py-1 ${field.width} align-top relative`}>
                                <Input
                                  type={field.type}
                                  value={row[field.key] || ''}
                                  onChange={(e) => updateAARRow(rowIndex, field.key, e.target.value)}
                                  className={`text-sm h-8 ${isRowVoided ? 'line-through' : ''} ${visibleError ? 'border-red-500 ring-1 ring-red-200' : ''} ${effectiveReadOnly ? 'text-gray-900 disabled:opacity-100 disabled:cursor-default' : ''}`}
                                  disabled={effectiveReadOnly || isRowVoided}
                                  title={visibleError || ''}
                                  data-testid={`aar-${field.key}-${rowIndex}`}
                                  {...(field.type === 'date' ? { max: todayEasternStr() } : {})}
                                />
                                {visibleError && (
                                  <p className="absolute mt-0.5 text-[10px] text-red-600 leading-tight pointer-events-none" data-testid={`aar-${field.key}-${rowIndex}-error`}>
                                    {visibleError}
                                  </p>
                                )}
                              </td>
                            );
                          })}
                          <td className="px-2 py-2 text-center sticky right-0 bg-white">
                            {!effectiveReadOnly && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeAARRow(rowIndex)}
                                className={`${isRowVoided ? 'text-gray-400' : 'text-red-500 hover:text-red-700 hover:bg-red-50'}`}
                                disabled={isRowVoided || formData.aar_work_details_items.filter(r => r.row_status !== 'VOIDED').length <= 1}
                                title={isSubmitted ? 'Void Row' : 'Delete Row'}
                                data-testid={`aar-remove-${rowIndex}`}
                              >
                                {isSubmitted && !isRowVoided ? <Ban className="w-4 h-4" /> : <Trash2 className="w-4 h-4" />}
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Add Row Button */}
            {!effectiveReadOnly && (
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                onClick={addAARRow}
                className="mt-2"
                data-testid="add-aar-row"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Line Item
              </Button>
            )}

            {/* Labor Rate (only for Operator+) */}
            {canSeeAmounts && (
              <div className="pt-4 border-t">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Labor Rate ($/hr)</Label>
                    <Input 
                      type="number"
                      step="0.01"
                      value={formData.labor_rate || ''}
                      onChange={(e) => updateField('labor_rate', parseFloat(e.target.value) || 0)}
                      placeholder="e.g., 75.00"
                      disabled={effectiveReadOnly}
                      data-testid="labor-rate"
                    />
                  </div>
                  <div className="md:col-span-2 flex items-end">
                    <div className="text-sm text-[#5A5A5A]">
                      Labor Total: <span className="font-medium text-[#323033]">${formData.labor_total.toFixed(2)}</span>
                      {getTotalHours() > 0 && formData.labor_rate > 0 && (
                        <span className="ml-2">({getTotalHours().toFixed(1)} hrs × ${formData.labor_rate}/hr)</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Section 4: Material Charges (Optional) */}
      <Card>
        <CardHeader className="pb-3 cursor-pointer" onClick={() => toggleSection('materials')}>
          <CardTitle className="text-base flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-[#005EF8]" />
              Job Code Material Charges
            </div>
            <div className="flex items-center gap-2">
              {canSeeAmounts && formData.material_total > 0 && (
                <span className="text-sm font-normal text-[#5A5A5A]">Total: ${formData.material_total.toFixed(2)}</span>
              )}
              {expandedSections.materials ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          </CardTitle>
        </CardHeader>
        {expandedSections.materials && (
          <CardContent>
            {getActiveJobCodes().length === 0 && formData.material_lines.length === 0 ? (
              <p className="text-xs text-amber-600 py-2" data-testid="material-empty-state">
                Job Code Material Charges require an existing BRC job code row. Add a work detail row first.
              </p>
            ) : formData.material_lines.length > 0 ? (
              <>
                {getActiveJobCodes().length === 0 && (
                  <p className="text-xs text-red-600 pb-2" data-testid="material-no-codes-warning">
                    No AAR Work Details job codes available. Fix or remove the material lines below before saving.
                  </p>
                )}
                {/* Header Row - New 6-field structure */}
                <div className={`grid grid-cols-12 gap-2 text-xs font-medium text-[#5A5A5A] pb-2 border-b`}>
                  <div className="col-span-2">Associated Job Code *</div>
                  <div className="col-span-2">Part # *</div>
                  <div className={canSeeAmounts ? "col-span-3" : "col-span-4"}>Description</div>
                  <div className={`${canSeeAmounts ? "col-span-1" : "col-span-2"} text-right`}>Qty</div>
                  {canSeeAmounts && (
                    <>
                      <div className="col-span-2 text-right">Unit Price</div>
                      <div className="col-span-1 text-right">Amount</div>
                    </>
                  )}
                  {!canSeeAmounts && <div className="col-span-1"></div>}
                  <div className="col-span-1"></div>
                </div>
                
                {/* Material Rows */}
                {formData.material_lines.map((line, index) => (
                  <MaterialLineRow 
                    key={line._key || `item-${index}`} 
                    line={line} 
                    index={index} 
                    onChange={updateMaterialLine} 
                    onRemove={removeMaterialLine}
                    readOnly={effectiveReadOnly}
                    canSeeAmounts={canSeeAmounts}
                    activeJobCodes={getActiveJobCodes()}
                    error={materialErrors[index]}
                  />
                ))}
              </>
            ) : (
              <p className="text-sm text-[#5A5A5A] text-center py-4">No material charges added</p>
            )}
            
            {/* Add Material Line Button */}
            {!effectiveReadOnly && getActiveJobCodes().length > 0 && (
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                onClick={addMaterialLine} 
                className="mt-3"
                data-testid="add-material-line"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Material Line
              </Button>
            )}
          </CardContent>
        )}
      </Card>


      {/* Section 5: Reporting Data (CID / Welding) */}
      <Card>
        <CardHeader className="pb-3 cursor-pointer" onClick={() => toggleSection('reportingData')}>
          <CardTitle className="text-base flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-[#005EF8]" />
              Reporting Data
            </div>
            <div className="flex items-center gap-2">
              {formData.reporting_data.length > 0 && (
                <span className="text-sm font-normal text-[#5A5A5A]">{formData.reporting_data.length} entr{formData.reporting_data.length === 1 ? 'y' : 'ies'}</span>
              )}
              {expandedSections.reportingData ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          </CardTitle>
        </CardHeader>
        {expandedSections.reportingData && (
          <CardContent>
            {formData.reporting_data.length > 0 ? (
              <div className="space-y-3">
                {formData.reporting_data.map((entry, index) => (
                  <div key={entry._key || entry.id || index} className="border rounded-lg p-3 bg-gray-50/50" data-testid={`reporting-entry-${index}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#005EF8]/10 text-[#005EF8]">{entry.data_type}</span>
                      {!effectiveReadOnly && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeReportingEntry(index)} className="h-6 w-6 p-0 text-red-500 hover:text-red-700" data-testid={`remove-reporting-${index}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-12 gap-2">
                      {/* Associated Job Code — shared by both types */}
                      <div className="col-span-4">
                        <Label className="text-xs">Associated Job Code *</Label>
                        {effectiveReadOnly ? (
                          <p className="text-sm font-medium mt-1">{entry.associated_job_code || '-'}</p>
                        ) : (
                          <select
                            className={`w-full mt-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm ${reportingErrors[index] ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                            value={entry.associated_job_code}
                            onChange={(e) => updateReportingEntry(index, { associated_job_code: e.target.value })}
                            data-testid={`reporting-job-code-${index}`}
                          >
                            <option value="">Select job code</option>
                            {getActiveJobCodes().map(jc => (
                              <option key={jc} value={jc}>{jc}</option>
                            ))}
                          </select>
                        )}
                        {reportingErrors[index] && (
                          <p className="text-xs text-red-600 mt-1" data-testid={`reporting-job-code-error-${index}`}>{reportingErrors[index]}</p>
                        )}
                      </div>

                      {/* CID-specific fields */}
                      {entry.data_type === 'CID' && (
                        <>
                          <div className="col-span-3">
                            <Label className="text-xs">Component Type *</Label>
                            {effectiveReadOnly ? (
                              <p className="text-sm font-medium mt-1">{entry.component_type || '-'}</p>
                            ) : (
                              <select
                                className="w-full mt-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                                value={entry.component_type || ''}
                                onChange={(e) => updateReportingEntry(index, { component_type: e.target.value })}
                                data-testid={`reporting-component-type-${index}`}
                              >
                                <option value="">Select component</option>
                                {CID_COMPONENT_TYPES.map(ct => (
                                  <option key={ct} value={ct}>{ct}</option>
                                ))}
                              </select>
                            )}
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs">Location/Position *</Label>
                            {effectiveReadOnly ? (
                              <p className="text-sm font-medium mt-1">{entry.location_position || '-'}</p>
                            ) : (
                              <Input
                                className="mt-1"
                                value={entry.location_position || ''}
                                onChange={(e) => {
                                  const val = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                                  updateReportingEntry(index, { location_position: val.slice(0, 2) });
                                }}
                                placeholder="e.g. A1"
                                maxLength={2}
                                data-testid={`reporting-location-position-${index}`}
                              />
                            )}
                          </div>
                          <div className="col-span-3">
                            <Label className="text-xs">CID # *</Label>
                            {effectiveReadOnly ? (
                              <p className="text-sm font-medium mt-1">{entry.cid_number || '-'}</p>
                            ) : (
                              <Input
                                className="mt-1"
                                value={entry.cid_number || ''}
                                onChange={(e) => {
                                  const val = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                                  updateReportingEntry(index, { cid_number: val.slice(0, 20) });
                                }}
                                placeholder="Alphanumeric only"
                                maxLength={20}
                                data-testid={`reporting-cid-number-${index}`}
                              />
                            )}
                          </div>
                        </>
                      )}

                      {/* Welding-specific fields */}
                      {entry.data_type === 'WELDING' && (
                        <>
                          <div className="col-span-3">
                            <Label className="text-xs">Process *</Label>
                            {effectiveReadOnly ? (
                              <p className="text-sm font-medium mt-1">{entry.process || '-'}</p>
                            ) : (
                              <select
                                className="w-full mt-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                                value={entry.process || ''}
                                onChange={(e) => updateReportingEntry(index, { process: e.target.value })}
                                data-testid={`reporting-process-${index}`}
                              >
                                <option value="">Select process</option>
                                {WELD_PROCESSES.map(p => (
                                  <option key={p} value={p}>{p}</option>
                                ))}
                              </select>
                            )}
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs">Position *</Label>
                            {effectiveReadOnly ? (
                              <p className="text-sm font-medium mt-1">{entry.position || '-'}</p>
                            ) : (
                              <select
                                className="w-full mt-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                                value={entry.position || ''}
                                onChange={(e) => updateReportingEntry(index, { position: e.target.value })}
                                data-testid={`reporting-position-${index}`}
                              >
                                <option value="">Select</option>
                                {WELD_POSITIONS.map(pos => (
                                  <option key={pos} value={pos}>{pos}</option>
                                ))}
                              </select>
                            )}
                          </div>
                          <div className="col-span-3">
                            <Label className="text-xs">WPS *</Label>
                            {effectiveReadOnly ? (
                              <p className="text-sm font-medium mt-1">{entry.wps || '-'}</p>
                            ) : (
                              <Input
                                className="mt-1"
                                value={entry.wps || ''}
                                onChange={(e) => updateReportingEntry(index, { wps: e.target.value })}
                                placeholder="WPS number"
                                maxLength={50}
                                data-testid={`reporting-wps-${index}`}
                              />
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    {/* Show auto-populated fields for Welding in read mode */}
                    {entry.data_type === 'WELDING' && (entry.car_number || entry.date || entry.verified_by) && (
                      <div className="flex gap-4 mt-2 text-xs text-[#808080]">
                        {entry.car_number && <span>Car: {entry.car_number}</span>}
                        {entry.date && <span>Date: {entry.date}</span>}
                        {entry.verified_by && <span>Verified By: {entry.verified_by}</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#5A5A5A] text-center py-4">No reporting data added</p>
            )}

            {/* Add Reporting Data Buttons */}
            {!effectiveReadOnly && (
              <div className="flex items-center gap-2 mt-3">
                {getActiveJobCodes().length > 0 ? (
                  <>
                    {REPORTING_DATA_TYPES.map(type => (
                      <Button
                        key={type.value}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addReportingEntry(type.value)}
                        data-testid={`add-reporting-${type.value.toLowerCase()}`}
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add {type.label}
                      </Button>
                    ))}
                  </>
                ) : (
                  <p className="text-xs text-amber-600">Reporting Data requires an existing BRC job code row. Add a work detail row first.</p>
                )}
              </div>
            )}
          </CardContent>
        )}
      </Card>


      {/* Section 5: Additional Charges */}
      <Card>
        <CardHeader className="pb-3 cursor-pointer" onClick={() => toggleSection('charges')}>
          <CardTitle className="text-base flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-[#005EF8]" />
              Additional Charges
            </div>
            <div className="flex items-center gap-2">
              {canSeeAmounts && formData.charges_total > 0 && (
                <span className="text-sm font-normal text-[#5A5A5A]">Total: ${formData.charges_total.toFixed(2)}</span>
              )}
              {expandedSections.charges ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          </CardTitle>
        </CardHeader>
        {expandedSections.charges && (
          <CardContent>
            {formData.additional_charges.length > 0 ? (
              <>
                {/* Header Row */}
                <div className="grid grid-cols-12 gap-2 text-xs font-medium text-[#5A5A5A] pb-2 border-b">
                  <div className="col-span-2">Type</div>
                  <div className="col-span-4">Description</div>
                  <div className="col-span-2 text-right">Qty</div>
                  {canSeeAmounts && (
                    <>
                      <div className="col-span-2 text-right">Unit Price</div>
                      <div className="col-span-1">Amount</div>
                    </>
                  )}
                  {!canSeeAmounts && <div className="col-span-3"></div>}
                  <div className="col-span-1"></div>
                </div>
                
                {/* Charge Rows */}
                {formData.additional_charges.map((charge, index) => (
                  <ChargeRow 
                    key={charge._key || `item-${index}`} 
                    charge={charge} 
                    index={index} 
                    onChange={updateChargeRow} 
                    onRemove={removeChargeRow}
                    readOnly={effectiveReadOnly}
                    canSeeAmounts={canSeeAmounts}
                  />
                ))}
              </>
            ) : (
              <p className="text-sm text-[#5A5A5A] text-center py-4">No additional charges</p>
            )}
            
            {/* Add Charge Button */}
            {!effectiveReadOnly && (
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                onClick={addChargeRow} 
                className="mt-3"
                data-testid="add-charge-row"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Charge
              </Button>
            )}
          </CardContent>
        )}
      </Card>

      {/* Section 6: Invoice To */}
      <Card>
        <CardHeader className="pb-3 cursor-pointer" onClick={() => toggleSection('invoice')}>
          <CardTitle className="text-base flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-[#005EF8]" />
              Invoice To
            </div>
            {expandedSections.invoice ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </CardTitle>
        </CardHeader>
        {expandedSections.invoice && (
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Company</Label>
                <Input 
                  value={formData.invoice_to_company}
                  onChange={(e) => updateField('invoice_to_company', e.target.value)}
                  placeholder="Company name"
                  disabled={effectiveReadOnly}
                  data-testid="invoice-company"
                />
              </div>
              <div>
                <Label>Contact Name</Label>
                <Input 
                  value={formData.invoice_to_contact}
                  onChange={(e) => updateField('invoice_to_contact', e.target.value)}
                  placeholder="Contact name"
                  disabled={effectiveReadOnly}
                  data-testid="invoice-contact"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={formData.invoice_to_email}
                  onChange={(e) => updateField('invoice_to_email', e.target.value)}
                  placeholder="email@example.com"
                  disabled={effectiveReadOnly}
                  data-testid="invoice-email"
                />
              </div>
              <div>
                <Label>Address</Label>
                <Input 
                  value={formData.invoice_to_address}
                  onChange={(e) => updateField('invoice_to_address', e.target.value)}
                  placeholder="Street address"
                  disabled={effectiveReadOnly}
                  data-testid="invoice-address"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label>City</Label>
                  <Input 
                    value={formData.invoice_to_city}
                    onChange={(e) => updateField('invoice_to_city', e.target.value)}
                    placeholder="City"
                    disabled={effectiveReadOnly}
                  />
                </div>
                <div>
                  <Label>State</Label>
                  <Input 
                    value={formData.invoice_to_state}
                    onChange={(e) => updateField('invoice_to_state', e.target.value)}
                    placeholder="State"
                    disabled={effectiveReadOnly}
                  />
                </div>
                <div>
                  <Label>ZIP</Label>
                  <Input 
                    value={formData.invoice_to_zip}
                    onChange={(e) => updateField('invoice_to_zip', e.target.value)}
                    placeholder="ZIP"
                    disabled={effectiveReadOnly}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Section 7: Notes */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea 
            value={formData.notes}
            onChange={(e) => updateField('notes', e.target.value)}
            placeholder="Additional notes..."
            rows={3}
            disabled={effectiveReadOnly}
            data-testid="brc-notes"
          />
        </CardContent>
      </Card>

      {/* Edit Reason for INVOICED BRCs */}
      {isInvoiced && !readOnly && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-amber-800 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Edit Reason Required
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea 
              value={formData.edit_reason}
              onChange={(e) => updateField('edit_reason', e.target.value)}
              placeholder="Provide reason for editing this INVOICED Billing Entry (e.g., 'Customer audit – refund for line X')"
              rows={2}
              required
              data-testid="edit-reason"
            />
          </CardContent>
        </Card>
      )}

      {/* Billing Summary (only for Operator+) */}
      {canSeeAmounts && (
        <Card className="bg-[#005EF8]/5 border-[#005EF8]/20">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-[#005EF8]" />
                <span className="font-medium text-[#323033]">Billing Summary</span>
              </div>
              <div className="text-right">
                <div className="text-sm text-[#5A5A5A] space-y-1">
                  <div>Labor ({getTotalHours().toFixed(1)} hrs): ${formData.labor_total.toFixed(2)}</div>
                  <div>Materials: ${formData.material_total.toFixed(2)}</div>
                  <div>Other Charges: ${formData.charges_total.toFixed(2)}</div>
                </div>
                <div className="text-xl font-bold text-[#005EF8] mt-2 pt-2 border-t">
                  Grand Total: ${formData.grand_total.toFixed(2)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      {!effectiveReadOnly && (
        <div className="flex justify-end gap-3 pt-4">
          {saveAttempted && formHasErrors && (
            <p className="self-center text-xs text-red-600" data-testid="aar-validation-banner">
              Fix the highlighted required fields before saving.
            </p>
          )}
          <Button type="button" variant="outline" onClick={onCancel} data-testid="cancel-brc">
            Cancel
          </Button>
          <Button 
            type="submit"
            // Save is never gated until the user has actually attempted save once.
            // After that, it stays disabled until the form is clean.
            disabled={saving || (saveAttempted && formHasErrors)}
            onClick={() => setSaveAttempted(true)}
            className="bg-[#005EF8] hover:bg-[#0047C2]"
            data-testid="save-brc"
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
            ) : (
              isNew ? 'Create Billing Entry' : 'Save Changes'
            )}
          </Button>
        </div>
      )}

      {/* Read-only close button */}
      {effectiveReadOnly && (
        <div className="flex justify-end pt-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Close
          </Button>
        </div>
      )}

      {/* Phase 2 — WDB / QVB modal selector */}
      <PersonnelPickerModal
        open={!!pickerState}
        onClose={closePersonnelPicker}
        field={pickerState?.field}
        facilityId={formData.responding_facility_id}
        api={api}
        selectedUserId={pickerState
          ? formData.aar_work_details_items[pickerState.rowIndex]?.[
              pickerState.field === 'WDB' ? 'wdb_user_id' : 'qvb_user_id'
            ]
          : null}
        onSelect={handlePersonnelSelect}
      />
    </form>
  );
}
